import { useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, FileText, Loader2 } from "lucide-react";

/**
 * Reusable drag-and-drop file uploader.
 * - `kind`: "image" | "pdf" — matches backend /api/admin/uploads?kind=…
 * - `value`: current stored URL (absolute or `/api/uploads/…`)
 * - `onChange(url)`: called with the new URL after upload, or "" when cleared.
 *
 * Also exposes a plain-text input so admins can still paste an external URL
 * (matches the original behaviour before uploads were available).
 */
export function FileDropzone({ kind = "image", value, onChange, testid }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const absolute = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/api/uploads/")) return `${API.replace(/\/api$/, "")}${url}`;
    return url;
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/admin/uploads?kind=${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const accept = kind === "pdf" ? ".pdf,application/pdf" : "image/*";
  const hasImage = kind === "image" && !!value;

  return (
    <div className="space-y-2" data-testid={testid || `dropzone-${kind}`}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed rounded-md p-4 text-center transition
          ${drag ? "border-[#1E3A8A] bg-blue-50" : "border-slate-300 bg-slate-50"}
          ${busy ? "opacity-70 pointer-events-none" : "hover:bg-slate-100"}`}
        data-testid={`${testid || `dropzone-${kind}`}-drop`}
      >
        {busy ? (
          <div className="flex flex-col items-center gap-1 text-sm text-slate-600">
            <Loader2 size={18} className="animate-spin" /> Uploading…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-sm text-slate-600">
            <UploadCloud size={20} className="text-[#1E3A8A]" />
            <div><b>Drag & drop</b> {kind === "pdf" ? "a PDF" : "an image"} here, or click to choose</div>
            <div className="text-xs text-slate-500">
              {kind === "pdf" ? "PDF up to 25 MB" : "JPG, PNG, WEBP, GIF up to 5 MB"}
            </div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testid || `dropzone-${kind}`}-input`}
        />
      </div>

      {value && (
        <div className="flex items-start gap-3 border border-slate-200 rounded-md p-2 bg-white">
          {hasImage ? (
            <img
              src={absolute(value)}
              alt="preview"
              className="h-16 w-24 object-cover rounded border border-slate-200"
              data-testid={`${testid || `dropzone-${kind}`}-preview`}
            />
          ) : (
            <div className="h-16 w-16 rounded bg-slate-100 flex items-center justify-center text-slate-500">
              <FileText size={20} />
            </div>
          )}
          <div className="flex-1 min-w-0 text-sm">
            <div className="truncate font-mono text-xs text-slate-600" title={value}>{value}</div>
            <a href={absolute(value)} target="_blank" rel="noreferrer" className="text-[#1E3A8A] text-xs font-semibold hover:underline">Open</a>
          </div>
          <button
            type="button"
            onClick={() => onChange("")}
            className="h-8 w-8 rounded border border-slate-200 text-red-600 flex items-center justify-center hover:bg-red-50"
            title="Remove"
            data-testid={`${testid || `dropzone-${kind}`}-clear`}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <input
        type="text"
        placeholder="…or paste an external URL"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 border border-slate-200 rounded text-xs font-mono"
        data-testid={`${testid || `dropzone-${kind}`}-url`}
      />
    </div>
  );
}
